import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNFLLeague } from '@/contexts/NFLLeagueContext';
import { Bomb } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface SabotageEvent {
  id: string;
  card_id: string;
  attacker_team_id: string;
  target_team_id: string;
  week: number;
  impact_points: number | null;
}

interface SabotageCardInfo {
  id: string;
  name: string;
  rarity: string;
}

const RARITY_STYLE: Record<string, { color: string; bg: string; border: string }> = {
  common: { color: 'text-muted-foreground', bg: 'bg-muted/20', border: 'border-border/40' },
  rare: { color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30' },
  epic: { color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30' },
  legendary: { color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
};

const CARD_EMOJIS: Record<string, string> = {
  'False Start Frenzy': '🏴',
  'Fumble Curse': '🤹',
  'Holding Call Horror': '🚩',
  'Intentional Grounding': '⚡',
  'Sack Attack': '💥',
  'Delay of Game Doom': '⏱️',
  'Pick Six Hex': '🎯',
  'Turnover Chain Snatch': '🔗',
  'Red Zone Robbery': '🔴',
  'Roughing the Passer': '💢',
  'Bench Bomb': '💣',
  'Bye Week Blitz': '🔥',
};

const PAGE_SIZE = 5;

export default function NFLSabotageCard() {
  const { currentLeague, teams } = useNFLLeague();
  const [events, setEvents] = useState<SabotageEvent[]>([]);
  const [cards, setCards] = useState<SabotageCardInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const teamMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const t of teams) map[t.id] = t.name;
    return map;
  }, [teams]);

  const cardMap = useMemo(() => {
    const map: Record<string, SabotageCardInfo> = {};
    for (const c of cards) map[c.id] = c;
    return map;
  }, [cards]);

  useEffect(() => {
    if (!currentLeague) { setIsLoading(false); return; }
    let cancelled = false;

    async function load() {
      // Get recent sabotage events (most recent 8)
      const { data: evData } = await supabase
        .from('nfl_sabotage_events' as any)
        .select('id, card_id, attacker_team_id, target_team_id, week, impact_points')
        .eq('league_id', currentLeague!.id)
        .eq('status', 'active')
        .order('week', { ascending: false })
        .limit(8);

      if (cancelled) return;

      if (evData && evData.length > 0) {
        setEvents(evData as SabotageEvent[]);

        // Fetch card info
        const cardIds = [...new Set((evData as any[]).map((e: any) => e.card_id))];
        const { data: cardData } = await supabase
          .from('nfl_sabotage_cards' as any)
          .select('id, name, rarity')
          .in('id', cardIds);

        if (!cancelled && cardData) {
          setCards(cardData as SabotageCardInfo[]);
        }
      }
      setIsLoading(false);
    }

    load();
    return () => { cancelled = true; };
  }, [currentLeague]);

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-foreground flex items-center gap-2">
          <Bomb className="h-4 w-4 text-damage" />
          Sabotage Feed
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <div className="h-5 w-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : events.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="h-10 w-10 rounded-full bg-damage/10 flex items-center justify-center mb-3">
              <Bomb className="h-5 w-5 text-damage/50" />
            </div>
            <p className="text-sm text-muted-foreground">No sabotages yet this week</p>
            <p className="text-xs text-muted-foreground mt-1">Sabotage events will appear here</p>
          </div>
        ) : (
          <div className="space-y-2">
            {events.slice(0, visibleCount).map(ev => {
              const card = cardMap[ev.card_id];
              const rarity = RARITY_STYLE[card?.rarity || 'common'] || RARITY_STYLE.common;
              const emoji = CARD_EMOJIS[card?.name || ''] || '💣';
              const attacker = teamMap[ev.attacker_team_id] || 'Unknown';
              const target = teamMap[ev.target_team_id] || 'Unknown';

              return (
                <div
                  key={ev.id}
                  className="flex items-start gap-2.5 py-1.5 border-b border-border/30 last:border-0"
                >
                  <span className="text-base shrink-0 mt-0.5">{emoji}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs font-medium text-foreground">{attacker}</span>
                      <span className="text-[10px] text-muted-foreground">used</span>
                      <Badge className={cn(
                        'text-[9px] px-1.5 py-0 border font-medium',
                        rarity.bg, rarity.color, rarity.border
                      )}>
                        {card?.name || 'Unknown'}
                      </Badge>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      vs {target} · Week {ev.week}
                    </p>
                  </div>
                </div>
              );
            })}
            {visibleCount < events.length && (
              <button
                onClick={() => setVisibleCount(prev => prev + PAGE_SIZE)}
                className="w-full py-2 text-xs text-primary hover:text-primary/80 transition-colors"
              >
                Show more sabotages ↓
              </button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
